'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useFinance } from '@/hooks/useFinance';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { EChart, type EChartsOption } from '@/components/charts/EChart';
import {
  chartAxisLabel,
  chartSplitLine,
  chartTooltipBase,
} from '@/components/charts/chart-options';
import { compactBrl } from '@/lib/format';
import { token } from '@/lib/theme/tokens';
import { useThemeColors } from '@/lib/theme/useThemeColors';
import type { GoalSummary } from '@/types';
import { formatYmShort, monthsUntilDeadline } from './helpers';
import { projectSavings, projectDebt } from './projection';

interface Props {
  goal: GoalSummary;
  simulatedContribution: number;
  simulatedTarget?: number | null;
  simulatedDeadline?: string | null;
  height?: number;
}

const MAX_MONTHS_FORWARD = 36;
const MIN_MONTHS_FORWARD = 12;
const HORIZON_BUFFER_MONTHS = 2;
const COMPACT_CHART_WIDTH = 440;
const HISTORY_MONTHS = 6;

// Area-gradient fades stay as fixed rgba alphas (decorative, not semantic tokens).
const COLOR_HISTORICAL_FADE = 'rgba(14,90,74,0.18)';
const COLOR_HISTORICAL_FADE_END = 'rgba(14,90,74,0.00)';
const COLOR_SIM_FADE = 'rgba(22,122,96,0.16)';
const COLOR_SIM_FADE_END = 'rgba(22,122,96,0.00)';

type ChartPoint = {
  monthIndex: number;
  label: string;
  historical: number | null;
  base: number | null;
  sim: number | null;
};

function monthLabel(monthsFromToday: number): string {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + monthsFromToday);
  return formatYmShort(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
}

function completionMonth(
  values: number[],
  target: number | null,
  isDebt: boolean,
  startBalance: number,
): number | null {
  if (isDebt) {
    if (startBalance <= 0) return 0;
    const index = values.findIndex((value) => value <= 0);
    return index >= 0 ? index + 1 : null;
  }

  if (target === null) return null;
  if (startBalance >= target) return 0;
  const index = values.findIndex((value) => value >= target);
  return index >= 0 ? index + 1 : null;
}

function resolveMonthsForward(candidates: Array<number | null>): number {
  const visibleWindow = candidates.filter(
    (value): value is number => typeof value === 'number' && Number.isFinite(value) && value >= 0,
  );

  if (visibleWindow.length === 0) return MAX_MONTHS_FORWARD;

  const monthsWithContext = Math.max(...visibleWindow) + HORIZON_BUFFER_MONTHS;
  return Math.min(MAX_MONTHS_FORWARD, Math.max(MIN_MONTHS_FORWARD, Math.ceil(monthsWithContext)));
}

function compactAxisInterval(labelCount: number): number {
  if (labelCount <= 6) return 0;
  return Math.max(1, Math.ceil(labelCount / 5) - 1);
}

export function TrajectoryChart({
  goal,
  simulatedContribution,
  simulatedTarget,
  simulatedDeadline,
  height = 240,
}: Props) {
  const { fetchGoalContributionHistory } = useFinance();
  const formatCurrency = useFormatCurrency();
  const theme = useThemeColors();
  const chartRef = useRef<HTMLDivElement | null>(null);
  const [chartWidth, setChartWidth] = useState(0);
  const [history, setHistory] = useState<{ ym: string; amount: number }[]>([]);

  useEffect(() => {
    const element = chartRef.current;
    if (!element) return undefined;

    const updateWidth = () => {
      const nextWidth = Math.round(element.getBoundingClientRect().width);
      setChartWidth((prev) => (prev === nextWidth ? prev : nextWidth));
    };

    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }

    const observer = new ResizeObserver((entries) => {
      const nextWidth = Math.round(entries[0]?.contentRect.width ?? 0);
      setChartWidth((prev) => (prev === nextWidth ? prev : nextWidth));
    });
    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchGoalContributionHistory(goal.id)
      .then((entries) => {
        if (!cancelled) setHistory(entries.slice(-HISTORY_MONTHS));
      })
      .catch(() => setHistory([]));
    return () => {
      cancelled = true;
    };
  }, [goal.id, fetchGoalContributionHistory]);

  const isDebt = goal.type === 'DebtPayoff';
  const originalContribution = goal.monthlyContribution ?? 0;
  const isModified = simulatedContribution !== originalContribution;

  const rawTarget = simulatedTarget ?? goal.targetAmount;
  const target =
    typeof rawTarget === 'number' && Number.isFinite(rawTarget) && rawTarget > 0
      ? rawTarget
      : null;

  const data = useMemo<ChartPoint[]>(() => {
    const today = goal.currentAmount ?? 0;
    const histCumulative: number[] = [];
    let acc = 0;
    for (const h of history) {
      acc += h.amount;
      histCumulative.push(acc);
    }
    const totalContributed = histCumulative[histCumulative.length - 1] ?? 0;
    const scaled = histCumulative.map((v) =>
      totalContributed > 0 ? (v / totalContributed) * today : today,
    );

    const startBalance = isDebt ? goal.currentBalance ?? target ?? 0 : today;
    const annualRate = goal.interestRateAnnual ?? 0;
    const deadline = simulatedDeadline === undefined ? goal.targetDate : simulatedDeadline;
    const deadlineMonths = deadline ? monthsUntilDeadline(deadline) : null;

    const baseProbe = isDebt
      ? projectDebt(startBalance, originalContribution, annualRate, MAX_MONTHS_FORWARD)
      : projectSavings(startBalance, originalContribution, MAX_MONTHS_FORWARD);
    const simProbe = isDebt
      ? projectDebt(startBalance, simulatedContribution, annualRate, MAX_MONTHS_FORWARD)
      : projectSavings(startBalance, simulatedContribution, MAX_MONTHS_FORWARD);
    const monthsForward = resolveMonthsForward([
      deadlineMonths,
      completionMonth(baseProbe, target, isDebt, startBalance),
      completionMonth(simProbe, target, isDebt, startBalance),
    ]);
    const baseFuture = baseProbe.slice(0, monthsForward);
    const simFuture = simProbe.slice(0, monthsForward);

    const points: ChartPoint[] = [];
    const histLen = scaled.length;

    if (histLen > 0) {
      for (let i = 0; i < histLen; i++) {
        const monthsFromToday = -(histLen - 1 - i);
        const value = isDebt ? startBalance : scaled[i];
        const atToday = monthsFromToday === 0;
        points.push({
          monthIndex: monthsFromToday,
          label: monthLabel(monthsFromToday),
          historical: value,
          base: atToday ? (isDebt ? startBalance : today) : null,
          sim: atToday ? (isDebt ? startBalance : today) : null,
        });
      }
    }

    if (histLen === 0) {
      points.push({
        monthIndex: 0,
        label: monthLabel(0),
        historical: isDebt ? startBalance : today,
        base: isDebt ? startBalance : today,
        sim: isDebt ? startBalance : today,
      });
    }

    for (let i = 0; i < monthsForward; i++) {
      points.push({
        monthIndex: i + 1,
        label: monthLabel(i + 1),
        historical: null,
        base: baseFuture[i],
        sim: simFuture[i],
      });
    }

    return points;
  }, [
    history,
    goal.currentAmount,
    goal.currentBalance,
    goal.interestRateAnnual,
    goal.targetDate,
    isDebt,
    originalContribution,
    simulatedContribution,
    simulatedDeadline,
    target,
  ]);

  const isCompact = chartWidth > 0 && chartWidth < COMPACT_CHART_WIDTH;

  const option = useMemo<EChartsOption>(() => {
    if (data.length === 0) return {};
    // Theme-driven chart colours (read live so a `[data-theme]` flip re-renders).
    const colorHistorical = theme.primary;
    const colorBase = token('--amount-neutral', '#9AA29E');
    const colorSim = token('--success', '#167A60');
    const colorTarget = token('--warning', '#A16207');
    const colorToday = token('--border-strong', '#C6CECA');
    const colorAxis = token('--border', '#DDE3E0');
    const colorAxisText = token('--text-secondary', '#5B625E');
    const labels = data.map((p) => p.label);
    const histSeries = data.map((p) => (p.historical === null ? null : p.historical));
    const baseSeries = data.map((p) => (p.base === null ? null : p.base));
    const simSeries = data.map((p) => (p.sim === null ? null : p.sim));
    const todayIdx = data.findIndex((p) => p.monthIndex === 0);
    const todayLabel = todayIdx >= 0 ? labels[todayIdx] : undefined;

    const seriesList: NonNullable<EChartsOption['series']> = [
      {
        name: 'Histórico',
        type: 'line',
        data: histSeries,
        connectNulls: false,
        showSymbol: false,
        smooth: 0.2,
        lineStyle: { color: colorHistorical, width: 2.5 },
        itemStyle: { color: colorHistorical },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: COLOR_HISTORICAL_FADE },
              { offset: 1, color: COLOR_HISTORICAL_FADE_END },
            ],
          },
        },
        z: 3,
      },
      {
        name: isModified ? 'Atual' : 'Projeção',
        type: 'line',
        data: baseSeries,
        connectNulls: false,
        showSymbol: false,
        smooth: 0.2,
        lineStyle: {
          color: colorBase,
          width: isModified ? 1.5 : 2,
          type: isModified ? 'dashed' : 'solid',
        },
        itemStyle: { color: colorBase },
        areaStyle: isModified
          ? undefined
          : {
              color: {
                type: 'linear',
                x: 0, y: 0, x2: 0, y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(154,162,158,0.16)' },
                  { offset: 1, color: 'rgba(154,162,158,0.00)' },
                ],
              },
            },
        markLine: {
          silent: true,
          symbol: 'none',
          animation: false,
          lineStyle: { color: colorToday, type: 'dashed' as const, width: 1 },
          label: {
            formatter: 'hoje',
            fontSize: 12,
            color: colorAxisText,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            position: 'insideStartTop' as const,
            padding: [2, 4, 2, 4],
            backgroundColor: '#FFFFFF',
            borderColor: colorAxis,
            borderWidth: 1,
            borderRadius: 4,
          },
          data: todayLabel ? [{ xAxis: todayLabel }] : [],
        },
        z: 2,
      },
    ];

    if (isModified) {
      seriesList.push({
        name: 'Simulação',
        type: 'line',
        data: simSeries,
        connectNulls: false,
        showSymbol: false,
        smooth: 0.2,
        lineStyle: { color: colorSim, width: 2.5 },
        itemStyle: { color: colorSim },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: COLOR_SIM_FADE },
              { offset: 1, color: COLOR_SIM_FADE_END },
            ],
          },
        },
        z: 4,
      });
    }

    // Target reference line — pushed onto the last series so the label sits on top
    if (target !== null) {
      const targetLine = {
        markLine: {
          silent: true,
          symbol: 'none',
          animation: false,
          lineStyle: { color: colorTarget, type: 'dashed' as const, width: 1.5 },
          label: {
            formatter: isDebt ? 'quitado' : 'meta',
            fontSize: 12,
            color: colorTarget,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            position: 'insideEndTop' as const,
            padding: [2, 6, 2, 6],
            backgroundColor: 'rgba(255,251,235,0.95)',
            borderColor: colorTarget,
            borderWidth: 1,
            borderRadius: 4,
          },
          data: [{ yAxis: target }],
        },
      };
      const last = seriesList[seriesList.length - 1] as Record<string, unknown> & { markLine?: unknown };
      // Preserve existing markLine on the base series (today marker) — attach target to a separate, invisible scatter so it doesn't clobber.
      seriesList.push({
        name: '',
        type: 'line',
        data: [],
        silent: true,
        showSymbol: false,
        ...targetLine,
      });
      void last;
    }

    const axisLabelInterval = isCompact ? compactAxisInterval(labels.length) : 'auto';

    return {
      tooltip: {
        ...chartTooltipBase,
        axisPointer: { type: 'line', lineStyle: { color: colorAxisText, type: 'dashed', opacity: 0.4 } },
        formatter: (raw: unknown) => {
          const params = (Array.isArray(raw) ? raw : [raw]) as Array<{
            seriesName?: string;
            value?: number | null;
            color?: string;
            axisValueLabel?: string;
          }>;
          if (!params.length) return '';
          const title = params[0].axisValueLabel ?? '';
          let html = `<div style="min-width:172px;"><div style="font-size:11px;font-weight:600;color:#F7F8F7;margin-bottom:6px;font-family:ui-monospace,monospace;">${title}</div>`;
          params.forEach((p) => {
            if (p.value === null || p.value === undefined || !p.seriesName) return;
            html += `<div style="display:flex;align-items:center;gap:8px;font-size:12px;color:#DDE3E0;padding:2px 0;">
              <span style="width:10px;height:2px;background:${p.color};border-radius:1px;flex-shrink:0;"></span>
              <span style="flex:1;">${p.seriesName}</span>
              <span style="color:#FFFFFF;font-variant-numeric:tabular-nums;font-weight:600;">${formatCurrency(p.value)}</span>
            </div>`;
          });
          html += '</div>';
          return html;
        },
      },
      grid: {
        left: isCompact ? 2 : 6,
        right: isCompact ? 8 : 16,
        top: 18,
        bottom: isCompact ? 12 : 6,
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: labels,
        boundaryGap: false,
        axisLabel: {
          ...chartAxisLabel,
          hideOverlap: true,
          interval: axisLabelInterval,
          margin: isCompact ? 8 : 10,
          rotate: isCompact ? 30 : 0,
        },
        axisLine: { lineStyle: { color: colorAxis } },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        axisLabel: { ...chartAxisLabel, formatter: (v: number) => compactBrl(v) },
        splitLine: chartSplitLine,
        axisLine: { show: false },
        axisTick: { show: false },
        scale: true,
      },
      series: seriesList,
    };
  }, [data, isCompact, isModified, isDebt, target, formatCurrency, theme]);

  if (data.length === 0) {
    return (
      <div
        style={{ width: '100%', height }}
        className="flex items-center justify-center rounded-lg border border-dashed border-border-subtle bg-surface-sunken text-sm text-text-secondary"
      >
        Sem dados suficientes para projetar
      </div>
    );
  }

  return (
    <div ref={chartRef} style={{ width: '100%', height }}>
      <EChart option={option} notMerge />
    </div>
  );
}
